import { HeatNavigatorWithSearch } from "@/pages/heat-report/components/controls/HeatNavigatorWithSearch";
import { useTableStyles } from "@/pages/heat-report/styles/useTableStyles";
import { getHeatStatus } from "@/pages/heat-report/utils/getHeatStatus";
import { useHeatReportContext } from "@/pages/meltshop/heat-report/HeatReportContext";
import { getMomentTz } from "@dexteel/mesf-core";
import {
	Box,
	Checkbox,
	FormControlLabel,
	FormGroup,
	Grid,
	InputAdornment,
	Paper,
	TextField,
	Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core";
import React from "react";

const moment = getMomentTz();

type Props = {
	getStringValue: (value: any) => string;
	setShowGradeModal: (show: boolean) => void;
	getDataFromAPI: (heatId: number | null) => void;
	onNext: () => void;
	onPrevious: () => void;
};

const useStyles = makeStyles((theme) => ({
	infoCardContainer: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		[theme.breakpoints.down("sm")]: {
			justifyContent: "flex-start",
		},
	},
	infoCard: {
		maxWidth: "200px",
		minWidth: "170px",
		width: "100%",
	},
}));

export const ChemistryHeatReportHeaderFields = ({
	getStringValue,
	setShowGradeModal,
	getDataFromAPI,
	onNext,
	onPrevious,
}: Props) => {
	const classes = useStyles();
	const tableClasses = useTableStyles();
	const { state, actions } = useHeatReportContext();

	const heat = state.chemistryHeatReport;

	const heatNavigatorData = heat
		? {
				HeatId: heat.HiddenHeatId || 0,
				HeatName: heat.HeatName || "",
				NextHeatId: heat.NextHeatId || null,
				PreviousHeatId: heat.PreviousHeatId || null,
				NextHeatName: heat.NextHeatName || null,
				PreviousHeatName: heat.PreviousHeatName || null,
				LadleName: heat.Ladle || null,
				HeatsOnLadle: heat.HeatCountLadle || null,
				TapDate: heat.ProductionDate || null,
				TapShift: heat.Shift || null,
				TapCrew: heat.Crew || null,
				OpenDate: null,
				OpenShift: null,
				OpenCrew: null,
				FinalStatus: heat.HeatStatusCode || null,
				SteelWeight: null,
				EAFProcessId: null,
				CASProcessId: null,
				Grade: heat.Grade || "",
			}
		: null;

	const handleShowExternalsHeats = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		const checked = event.target.checked;
		actions.setShowExternalsHeats(checked);
	};

	return (
		<Paper elevation={0} style={{ border: "1px solid #ccc" }}>
			<Box padding={1}>
				<Grid item xs={12} container spacing={2}>
					<Grid
						item
						xs={12}
						sm={6}
						md={3}
						container
						spacing={1}
						key="col-heat-navigator"
					>
						<Grid item xs={12} key="heat-navigator" alignContent="center">
							{heatNavigatorData && (
								<HeatNavigatorWithSearch
									value={heatNavigatorData}
									onChange={(heatId) => {
										getDataFromAPI(heatId);
									}}
									onBlur={() => {}}
									showNext={!!heat?.NextHeatId}
									showPrevious={!!heat?.PreviousHeatId}
									onNext={onNext}
									onPrevious={onPrevious}
									onLast={() => {
										getDataFromAPI(null);
									}}
								/>
							)}
						</Grid>
						{/* 
                    <Grid item xs={12} container justifyContent="center" key="external-heats-checkbox">
                           <FormGroup>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={state.showExternalsHeats}
                                        color="primary"
                                        onChange={handleShowExternalsHeats}
                                        size="small"
                                    />
                                }
                                style={{ userSelect: "none" }}
                                label={<Typography style={{ fontSize: "0.8rem" }}>Show external heats</Typography>}
                            />
                        </FormGroup>
                    </Grid>                    */}
					</Grid>

					<Grid
						item
						xs={12}
						sm={6}
						md={3}
						container
						spacing={1}
						key="col-main-data-1"
					>
						<Grid
							item
							xs={12}
							container
							direction="row"
							spacing={1}
							alignContent="flex-start"
						>
							<Grid item xs={12} key="heat-status">
								<TextField
									label="Heat Status"
									value={getHeatStatus(heat?.HeatStatusCode ?? "").name}
									variant="outlined"
									fullWidth
									disabled
									InputProps={{
										endAdornment: (
											<InputAdornment position="start">
												<div
													style={{
														width: "26px",
														height: "26px",
														borderRadius: 4,
														display: "inline-flex",
														alignItems: "center",
														justifyContent: "center",
														color: "#fff",
														fontWeight: "bold",
														backgroundColor: heat?.HeatStatusCode
															? getHeatStatus(heat.HeatStatusCode).color
															: "#ccc",
													}}
												>
													{heat?.HeatStatusCode}
												</div>
											</InputAdornment>
										),
									}}
								/>
							</Grid>
							<Grid item xs={12} key="date-shift-crew">
								<TextField
									label="Production Date"
									value={
										heat?.ProductionDate
											? `${moment(heat?.ProductionDate).format("MM/DD/YYYY")} - Shift ${heat?.Shift} - Crew ${heat?.Crew}`
											: ""
									}
									variant="outlined"
									fullWidth
									disabled
								/>
							</Grid>
						</Grid>
					</Grid>

					<Grid
						item
						xs={12}
						sm={6}
						md={3}
						container
						spacing={1}
						key="col-main-data-2"
					>
						<Grid
							item
							xs={12}
							container
							direction="row"
							spacing={1}
							alignContent="flex-start"
						>
							<Grid item xs={12} key="programmed-grade">
								<TextField
									label="Programmed Grade"
									value={getStringValue(heat?.ProgrammedGrade)}
									variant="outlined"
									fullWidth
									disabled
								/>
							</Grid>
							<Grid item xs={12} key="cast-grade">
								<TextField
									label="Cast Grade"
									value={getStringValue(heat?.Grade)}
									variant="outlined"
									fullWidth
									disabled={
										heat?.HeatStatusCode !== "F" && heat?.HeatStatusCode !== "O"
									}
									onClick={() => {
										setShowGradeModal(true);
									}}
									InputProps={{
										style: { cursor: "pointer" },
									}}
								/>
							</Grid>
						</Grid>
					</Grid>

					<Grid
						item
						xs={12}
						sm={6}
						md={3}
						container
						spacing={1}
						key="col-main-data-3"
					>
						<Grid
							item
							xs={12}
							container
							direction="row"
							spacing={1}
							alignContent="flex-start"
						>
							<Grid item xs={12} key="sections">
								<TextField
									label="Sections"
									value={getStringValue(heat?.Sections)}
									variant="outlined"
									fullWidth
									disabled
								/>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Box>
		</Paper>
	);
};
